import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { solicitudesAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Send, Upload, CheckCircle2, Loader2, QrCode, Plane } from 'lucide-react';

export default function SolicitudFormPublico() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [tipo, setTipo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [datos, setDatos] = useState({});
    const [archivos, setArchivos] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [resultado, setResultado] = useState(null);

    useEffect(() => {
        solicitudesAPI.getFormulario(slug || 'vuelo-rpas')
            .then(r => {
                setTipo(r.data);
                const initial = {};
                r.data.campos?.forEach(c => { initial[c.key] = ''; });
                setDatos(initial);
            })
            .catch(() => toast.error('Formulario no encontrado'))
            .finally(() => setLoading(false));
    }, [slug]);

    const handleChange = (key, value) => {
        setDatos(prev => ({ ...prev, [key]: value }));
    };

    const handleFile = (key, file) => {
        setArchivos(prev => ({ ...prev, [key]: file }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validar campos requeridos
        for (const campo of tipo.campos) {
            if (campo.requerido && !datos[campo.key]) {
                toast.error(`Campo obligatorio: ${campo.label}`);
                return;
            }
        }
        // Validar documentos obligatorios
        for (const doc of tipo.documentos_obligatorios) {
            if (!archivos[doc.key]) {
                toast.error(`Documento obligatorio: ${doc.label}`);
                return;
            }
        }

        setSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('tipo_solicitud_id', tipo.id);
            formData.append('datos_json', JSON.stringify(datos));
            formData.append('solicitante_nombre', datos.operador_nombre || datos.solicitante_nombre || '');
            formData.append('solicitante_email', datos.operador_email || datos.solicitante_email || '');
            formData.append('solicitante_empresa', datos.operador_empresa || datos.solicitante_empresa || '');

            const keys = [];
            for (const [key, file] of Object.entries(archivos)) {
                formData.append('archivos', file);
                keys.push(key);
            }
            formData.append('archivos_keys', JSON.stringify(keys));

            const resp = await solicitudesAPI.crear(formData);
            setResultado(resp.data);
            toast.success('Solicitud enviada exitosamente');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al enviar solicitud');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="flex items-center justify-center h-screen"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
    if (!tipo) return <div className="text-center py-12">Formulario no disponible</div>;

    // Post-envío: mostrar confirmación con QR
    if (resultado) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <Card className="max-w-md w-full text-center">
                    <CardContent className="py-8 space-y-4">
                        {empresaLogo && <img src={empresaLogo} alt={empresaNombre} className="h-10 mx-auto object-contain" />}
                        <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto" />
                        <h2 className="text-xl font-bold">Solicitud Enviada</h2>
                        <p className="text-muted-foreground">{resultado.message}</p>
                        <div className="border rounded-lg p-4 bg-muted/30">
                            <p className="text-sm text-muted-foreground mb-2">Código QR de verificación:</p>
                            <img
                                src={solicitudesAPI.getQrUrl(resultado.id)}
                                alt="QR Verificación"
                                className="mx-auto w-48 h-48"
                            />
                            <p className="text-xs text-muted-foreground mt-2 break-all">{resultado.verificacion_url}</p>
                        </div>
                        <Button onClick={() => { setResultado(null); setDatos({}); setArchivos({}); }} variant="outline">
                            Enviar otra solicitud
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const empresaLogo = tipo.empresa?.logo_url_light ? `${backendUrl}${tipo.empresa.logo_url_light}` : null;
    const empresaNombre = tipo.empresa?.nombre_corto || tipo.empresa?.nombre || 'Protección Industrial';

    const renderField = (campo) => {
        const { key, label, tipo: fieldType, requerido, opciones } = campo;
        const common = { id: key, required: requerido, 'data-testid': `field-${key}` };

        switch (fieldType) {
            case 'select':
                return React.createElement('select', {
                    ...common,
                    className: "w-full border rounded-md p-2 bg-background text-sm",
                    value: datos[key] || '',
                    onChange: e => handleChange(key, e.target.value),
                }, [
                    React.createElement('option', { key: '__empty', value: '' }, 'Seleccionar...'),
                    ...(opciones || []).map(o => React.createElement('option', { key: o, value: o }, o))
                ]);
            case 'textarea':
                return (
                    <textarea {...common} className="w-full border rounded-md p-2 bg-background text-sm min-h-[80px]"
                        value={datos[key] || ''} onChange={e => handleChange(key, e.target.value)} />
                );
            case 'date':
                return <Input {...common} type="date" value={datos[key] || ''} onChange={e => handleChange(key, e.target.value)} />;
            case 'time':
                return <Input {...common} type="time" value={datos[key] || ''} onChange={e => handleChange(key, e.target.value)} />;
            case 'number':
                return <Input {...common} type="number" step="any" value={datos[key] || ''} onChange={e => handleChange(key, e.target.value)} />;
            case 'email':
                return <Input {...common} type="email" value={datos[key] || ''} onChange={e => handleChange(key, e.target.value)} />;
            default:
                return <Input {...common} type="text" value={datos[key] || ''} onChange={e => handleChange(key, e.target.value)} />;
        }
    };

    return (
        <div className="min-h-screen bg-background">
            <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6">
                {/* Header */}
                <div className="text-center space-y-3 pt-4">
                    {empresaLogo ? (
                        <img src={empresaLogo} alt={empresaNombre} className="h-14 mx-auto object-contain" />
                    ) : (
                        <Plane className="h-10 w-10 text-primary mx-auto" />
                    )}
                    <h1 className="text-2xl font-bold text-primary">{tipo.nombre}</h1>
                    {tipo.descripcion && <p className="text-sm text-muted-foreground max-w-lg mx-auto">{tipo.descripcion}</p>}
                    <p className="text-xs text-muted-foreground">{empresaNombre}</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Campos dinámicos */}
                    <Card>
                        <CardHeader className="pb-3"><CardTitle className="text-base">Información de la Solicitud</CardTitle></CardHeader>
                        <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {tipo.campos.map(campo => (
                                <div key={campo.key} className={campo.tipo === 'textarea' ? 'col-span-full' : ''}>
                                    <Label htmlFor={campo.key}>
                                        {campo.label} {campo.requerido && <span className="text-red-500">*</span>}
                                    </Label>
                                    {renderField(campo)}
                                </div>
                            ))}
                        </CardContent>
                    </Card>

                    {/* Documentos obligatorios */}
                    <Card>
                        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Upload className="h-4 w-4" /> Documentación Requerida</CardTitle></CardHeader>
                        <CardContent className="space-y-4">
                            {tipo.documentos_obligatorios.map(doc => (
                                <div key={doc.key} className="border rounded-lg p-3">
                                    <Label htmlFor={`file-${doc.key}`}>
                                        {doc.label} <span className="text-red-500">*</span>
                                    </Label>
                                    <p className="text-xs text-muted-foreground mb-2">
                                        Formatos aceptados: {doc.formatos?.join(', ')}
                                    </p>
                                    <input
                                        id={`file-${doc.key}`}
                                        type="file"
                                        accept={doc.formatos?.join(',')}
                                        onChange={e => handleFile(doc.key, e.target.files?.[0])}
                                        className="text-sm"
                                        data-testid={`file-${doc.key}`}
                                    />
                                    {archivos[doc.key] && (
                                        <p className="text-xs text-green-600 mt-1">
                                            {archivos[doc.key].name} ({(archivos[doc.key].size / 1024).toFixed(0)} KB)
                                        </p>
                                    )}
                                </div>
                            ))}
                        </CardContent>
                    </Card>

                    <Button type="submit" className="w-full" size="lg" disabled={submitting} data-testid="submit-solicitud">
                        {submitting ? <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Enviando...</> : <><Send className="h-5 w-5 mr-2" /> Enviar Solicitud</>}
                    </Button>
                </form>
            </div>
        </div>
    );
}
